import { Hex, PublicKey, Signature } from "@iden3/js-crypto";
import { Merklizer, Path } from "@iden3/js-jsonld-merklization";
import * as snarkjs from "snarkjs";

import { Keccak256Hasher } from "../utils/hasher";
import {
  CIRCUIT_BJJ_WASM_PATH,
  CIRCUIT_BJJ_ZKEY_PATH,
  CIRCUIT_WASM_PATH,
  CIRCUIT_ZKEY_PATH,
  CREDENTIAL_SUBJECT_PATH,
  SCORE_PATH,
} from "../constants";
import { VerifiableCredential, ZkProof } from "../domain";

async function prepareInputs(vc: VerifiableCredential) {
  const subjectPath = new Path(
    [CREDENTIAL_SUBJECT_PATH],
    new Keccak256Hasher()
  );
  const scorePath = new Path(
    [CREDENTIAL_SUBJECT_PATH, SCORE_PATH],
    new Keccak256Hasher()
  );
  const { proof, ...vcWithoutProof } = vc;
  const verifiableCredentialMT = await Merklizer.merklizeJSONLD(
    JSON.stringify(vcWithoutProof),
    { hasher: new Keccak256Hasher() }
  );
  const credentialSubjectProof = await verifiableCredentialMT?.proof(
    subjectPath
  );
  const scoreProof = await verifiableCredentialMT?.proof(scorePath);

  return {
    root: (await verifiableCredentialMT?.root()).bigInt().toString(),
    credentialSubject: await credentialSubjectProof?.value
      ?.mtEntry()
      .then((entry) => entry.toString()),
    credentialSubjectPathMtp: prepareArray(
      credentialSubjectProof?.proof
        .allSiblings()
        .map((sibling) => sibling.bigInt().toString()),
      20
    ),
    fieldNotExists: "0",
    fieldValue: vc.credentialSubject.score.toString(),
    fieldKey: await scorePath.mtEntry().then((entry) => entry.toString()),
    fieldMtp: prepareArray(
      scoreProof?.proof
        .allSiblings()
        .map((sibling) => sibling.bigInt().toString()),
      20
    ),
    fieldNoAux: "0",
    fieldAuxHi: "0",
    fieldAuxHv: "0",

    operator: 1,
    queryValue: [vc.credentialSubject.score.toString(), "0", "0", "0"],
  };
}

async function prepareBJJInputs(vc: VerifiableCredential) {
  const subjectPath = new Path(
    [CREDENTIAL_SUBJECT_PATH],
    new Keccak256Hasher()
  );
  const scorePath = new Path(
    [CREDENTIAL_SUBJECT_PATH, SCORE_PATH],
    new Keccak256Hasher()
  );
  const { proof, ...vcWithoutProof } = vc;
  const verifiableCredentialMT = await Merklizer.merklizeJSONLD(
    JSON.stringify(vcWithoutProof),
    { hasher: new Keccak256Hasher() }
  );
  const credentialSubjectProof = await verifiableCredentialMT?.proof(
    subjectPath
  );
  const scoreProof = await verifiableCredentialMT?.proof(scorePath);
  const pubKey = PublicKey.newFromHex(proof.verificationMethod);
  const pubKeyX = pubKey.p[0].toString();
  const pubKeyY = pubKey.p[1].toString();
  const sig = Signature.newFromCompressed(Hex.decodeString(proof.proofValue));

  return {
    root: (await verifiableCredentialMT?.root()).bigInt().toString(),
    pubKeyX: pubKeyX,
    pubKeyY: pubKeyY,
    rootSignatureR8x: sig.R8[0].toString(),
    rootSignatureR8y: sig.R8[1].toString(),
    rootSignatureS: sig.S.toString(),
    credentialSubject: await credentialSubjectProof?.value
      ?.mtEntry()
      .then((entry) => entry.toString()),
    credentialSubjectPathMtp: prepareArray(
      credentialSubjectProof?.proof
        .allSiblings()
        .map((sibling) => sibling.bigInt().toString()),
      20
    ),
    fieldNotExists: "0",
    fieldValue: vc.credentialSubject.score.toString(),
    fieldKey: await scorePath.mtEntry().then((entry) => entry.toString()),
    fieldMtp: prepareArray(
      scoreProof?.proof
        .allSiblings()
        .map((sibling) => sibling.bigInt().toString()),
      20
    ),
    fieldNoAux: "0",
    fieldAuxHi: "0",
    fieldAuxHv: "0",

    operator: 1,
    queryValue: [vc.credentialSubject.score.toString(), "0", "0", "0"],
  };
}

function prepareArray(arr: any, length: number): string[] {
  for (let i = arr.length; i < length; i++) {
    arr.push("0");
  }

  return arr;
}

export const generateProof = async (
  verifiableCredential: VerifiableCredential
): Promise<ZkProof> => {
  try {
    if (verifiableCredential.proof.cryptosuite === "merklize-sponge6-eth") {
      const inputs = await prepareInputs(verifiableCredential);
      const result = (await snarkjs.groth16.fullProve(
        inputs,
        CIRCUIT_WASM_PATH,
        CIRCUIT_ZKEY_PATH
      )) as ZkProof;

      return result;
    } else {
      const inputs = await prepareBJJInputs(verifiableCredential);

      return (await snarkjs.groth16.fullProve(
        inputs,
        CIRCUIT_BJJ_WASM_PATH,
        CIRCUIT_BJJ_ZKEY_PATH
      )) as ZkProof;
    }
  } catch (e) {
    console.error(e);
    throw new Error("An error occurred while generating the zk proof");
  }
};
